import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ContentChild, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, signal, SimpleChanges, TemplateRef, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { UiExercise } from 'models/workout-ui-models';
import { CsCardListComponent, CsCardListPlacement } from 'components/cs-card-list/cs-card-list';
import { ExerciseStatusBadgeType } from 'components/cs-exercise-status-badge/cs-exercise-status-badge';
import { hasClientSmallScreen } from 'environments/environment-helpers';
import { CardListDragService, CardListDragConfig } from 'services/card-list-drag.service';
import { computeExerciseBadgeNumbers } from 'services/exercise-badge-numbers';
import { ComboSetNumbers, ComboSetPosition, comboSetPosition, joinComboSet, renumberComboSets, settleComboSetsAfterDrag } from 'services/combo-set-rules';

export enum InsertActionType {
  Exercise = 'exercise'
}

export interface EditListItem {
  type: 'exercise';
  exercise: UiExercise;
}

@Component({
  selector: 'cs-exercise-list',
  templateUrl: 'cs-exercise-list.html',
  host: { 'class': 'exercise-list' },
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CardListDragService]
})
export class CsExerciseListComponent implements OnChanges, OnDestroy {
  protected LL = LL;
  @Input() exercises: UiExercise[] = [];
  @Input() foldable = false;                 // enable card expansion
  @Input() showMoreButton = true;
  // #4142: an EXPANDED card shows its menu button top right in the header (mock boards H/J);
  // its click is reported separately because it opens the full exercise menu
  @Input() showExpandedMoreButton = false;
  // #4142: hide an expanded card's fold arrow (tapping the header still folds it); passed to cs-card
  @Input() hideExpandedFoldIcon = false;
  /** #4142 compact feedback: draw finished exercises' badges filled; passed to cs-exercise-status-badge */
  @Input() fillCompletedBadge = false;
  @Input() editable = false;                 // enable edit mode with drop zone
  /**
   * #4142 review fixes: the exercises whose card draws the line under its name from the parent's
   * `descriptionContent` template instead of `UiExercise.description`, and folds with a down chevron
   * (a finished exercise with the compact switch on). Left unset, every card is drawn as before
   */
  @Input() usesCustomDescription?: (exercise: UiExercise) => boolean;

  // optional content templates (provided by parent when needed)
  @ContentChild('expandedContent') expandedTemplate: TemplateRef<any>;
  @ContentChild('collapsedContent') collapsedTemplate: TemplateRef<any>;
  @ContentChild('descriptionContent') descriptionTemplate: TemplateRef<any>;

  /** whether this card's line under the name comes from the parent's `descriptionContent` template */
  hasCustomDescription(exercise: UiExercise): boolean {
    return !!this.descriptionTemplate && !!this.usesCustomDescription?.(exercise);
  }

  @ViewChild(CsCardListComponent) cardList: CsCardListComponent;

  // events
  @Output() exerciseMoreOptionsClick = new EventEmitter<{ event: Event, exercise: UiExercise }>();
  @Output() exerciseExpandedMoreClick = new EventEmitter<{ event: Event, exercise: UiExercise }>();
  @Output() exerciseExpandedChange = new EventEmitter<{ exercise: UiExercise, expanded: boolean }>();
  @Output() exerciseHighlightedChange = new EventEmitter<{ exercise: UiExercise, highlighted: boolean }>();
  @Output() exerciseHeaderClick = new EventEmitter<UiExercise>();
  @Output() exercisesReordered = new EventEmitter<UiExercise[]>();
  @Output() insertActionClick = new EventEmitter<{ insertAtIndex: number, action: InsertActionType }>();

  // internal
  hoveredInsertIndex: number | null = null;
  // top of each insert strip in px, by insert index (1 = after the first card). Left out for a
  // card the list has not placed yet, so no strip is drawn over the cards before they settle.
  // a signal, not a plain field: cs-card-list often reports a new layout from its
  // ngAfterContentChecked, which runs inside this view's own check, after the template has
  // been read. markForCheck() from there is dropped (Angular does not re-check a view marked
  // during its own check), but a signal the template read is re-run in the same tick.
  insertZoneTops = signal<(number | undefined)[]>([]);
  editListItems: EditListItem[] = [];
  private badgeNumbers: string[] = [];
  private _wasComboSetDrop = false;

  constructor(
    private elementRef: ElementRef,
    public cdRef: ChangeDetectorRef,
    public dragService: CardListDragService
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['exercises'] || changes['editable']) {
      // always build editListItems - used for both edit and read-only modes
      this.buildEditListItems();
    }
  }

  ngOnDestroy() {
    this.dragService.destroy();
  }

  // look up precomputed badge number for the given exercise
  getExerciseNumber(exercise: UiExercise): string {
    const index = this.exercises.indexOf(exercise);
    return index >= 0 && index < this.badgeNumbers.length ? this.badgeNumbers[index] : '';
  }

  private buildEditListItems(): void {
    if (!this.exercises || this.exercises.length === 0) {
      this.editListItems = [];
      this.badgeNumbers = [];
      return;
    }

    this.editListItems = this.exercises.map(exercise => ({
      type: 'exercise' as const,
      exercise
    }));
    this.badgeNumbers = computeExerciseBadgeNumbers(this.exercises);
  }

  // write the combo set numbers the rules returned back onto the exercises, by index.
  // only a changed number is written, so an exercise that never had one is left untouched.
  private applyComboSetNumbers(exercises: UiExercise[], numbers: ComboSetNumbers): void {
    exercises.forEach((exercise, i) => {
      if (exercise.comboSetNumber !== numbers[i]) {
        exercise.comboSetNumber = numbers[i];
      }
    });
  }

  // Normalize combo set numbers to be sequential (1, 2, 3...)
  normalizeComboSetNumbers(): void {
    if (!this.exercises || this.exercises.length === 0) return;

    this.applyComboSetNumbers(this.exercises, renumberComboSets(this.exercises));

    this.exercisesReordered.emit(this.exercises);
    this.cdRef.markForCheck();
  }

  // Update exercises array to match editListItems order after drag
  recalculateGroupsFromEditList(draggedFinalIndex: number, wasComboSetDrop = false): void {
    const reorderedExercises = this.editListItems.map(item => item.exercise);

    // update in place to preserve the array reference
    this.exercises.length = 0;
    this.exercises.push(...reorderedExercises);

    // auto-join between members, leave at an edge (unless a combo set drop), dissolve lone members
    this.applyComboSetNumbers(this.exercises, settleComboSetsAfterDrag(this.exercises, draggedFinalIndex, wasComboSetDrop));

    // normalize to ensure sequential numbering
    this.normalizeComboSetNumbers();

    // rebuild editListItems so @for gets a fresh array reference
    // (comboSetNumber mutations on exercise objects don't trigger @for reconciliation)
    this.buildEditListItems();

    // force synchronous re-render so badge numbers and bracket classes
    // update immediately after drag — markForCheck() alone may not trigger
    // re-rendering in time due to the drag service's direct DOM writes
    this.cdRef.detectChanges();
  }

  // Handle combo set creation when an exercise is dropped on another card
  private handleComboSetDrop(draggedIndex: number, targetIndex: number): void {
    const listExercises = this.editListItems.map(item => item.exercise);
    this.applyComboSetNumbers(listExercises, joinComboSet(listExercises, draggedIndex, targetIndex));
  }

  onExpandedChange(exercise: UiExercise, expanded: boolean) {
    exercise.expanded.set(expanded);
    this.exerciseExpandedChange.emit({ exercise, expanded });
  }

  onHighlightedChange(exercise: UiExercise, highlighted: boolean) {
    exercise.highlighted.set(highlighted);
    this.exerciseHighlightedChange.emit({ exercise, highlighted });
  }

  onMoreOptionsClick(event: Event, exercise: UiExercise) {
    event.stopPropagation();
    this.exerciseMoreOptionsClick.emit({ event, exercise });
  }

  // the menu button of an expanded card; stops the click so it does not fold the card
  onExpandedMoreClick(event: Event, exercise: UiExercise) {
    event.stopPropagation();
    this.exerciseExpandedMoreClick.emit({ event, exercise });
  }

  onHeaderClick(exercise: UiExercise) {
    // ignore clicks that happen right after a drag operation
    if (this.dragService.didDragJustEnd()) {
      return;
    }
    this.exerciseHeaderClick.emit(exercise);
  }

  private getDragConfig(): CardListDragConfig {
    return {
      cardSelector: 'cs-card.exercise-card',
      topBoundSelector: 'cs-card-list-row.insert-row',
      bottomBoundSelector: '[footer-cards]',
      hostElement: this.elementRef.nativeElement,
      listComponent: this.cardList,
      changeDetector: this.cdRef,
      onReorder: (fromIndex, toIndex) => {
        if (this.editable) {
          const [removed] = this.editListItems.splice(fromIndex, 1);
          this.editListItems.splice(toIndex, 0, removed);
        } else {
          const [removed] = this.exercises.splice(fromIndex, 1);
          this.exercises.splice(toIndex, 0, removed);
        }
      },
      badgeItems: this.editable ? this.editListItems.map(item => ({
        comboSetNumber: item.exercise.comboSetNumber
      })) : undefined,
      onComboSetDrop: this.editable ? (draggedIndex, targetIndex) => {
        this.handleComboSetDrop(draggedIndex, targetIndex);
        this._wasComboSetDrop = true;
      } : undefined,
      onDragEnd: (draggedFinalIndex) => {
        if (this.editable) {
          this.recalculateGroupsFromEditList(draggedFinalIndex, this._wasComboSetDrop);
          this._wasComboSetDrop = false;
        } else {
          this.exercisesReordered.emit(this.exercises);
        }
      }
    };
  }

  // Edit mode drag handler via drag handle (desktop only - no long press delay)
  onEditItemDragStart(event: PointerEvent, _item: EditListItem, index: number) {
    this.dragService.startDragHandle(event, index, this.getDragConfig());
  }

  // Mobile long-press drag on entire card (desktop uses drag-zone handle)
  onEditItemLongPress(event: PointerEvent, index: number) {
    this.dragService.startLongPress(event, index, this.getDragConfig());
  }

  trackByExerciseId(_index: number, exercise: UiExercise): string {
    return exercise.exercise._id;
  }

  trackByEditListItem(_index: number, item: EditListItem): string {
    // the exercise id alone is not unique in a workout: an exercise changed after sets were logged
    // keeps its sets in an entry of its own (#5507), so changing back gives the same exercise two
    // entries. Their sub-slot ids differ. The exercise id stays in the key so that changing an
    // entry's exercise still gives it a fresh card, as it always did.
    const subSlotId = item.exercise.logExercise?.subSlotId;
    return subSlotId ? `${subSlotId}|${item.exercise.exercise._id}` : item.exercise.exercise._id;
  }

  // Helper methods to read signal state
  isExpanded(e: UiExercise): boolean {
    return e.expanded();
  }

  isHighlighted(e: UiExercise): boolean {
    return e.highlighted();
  }

  isLoading(e: UiExercise): boolean {
    return e.loading();
  }

  // in edit mode, always show number badge (no completion status)
  getBadgeType(e: UiExercise): ExerciseStatusBadgeType {
    if (this.editable) {
      return ExerciseStatusBadgeType.Number;
    }
    return e.getStatusBadgeType();
  }

  isSmallScreen(): boolean {
    return hasClientSmallScreen();
  }

  // returns bracket position for combo set exercises (edit mode only)
  getComboSetPosition(exercise: UiExercise): ComboSetPosition | null {
    if (exercise.comboSetNumber === undefined) return null;
    return comboSetPosition(this.exercises, this.exercises.indexOf(exercise));
  }

  // Insert row click handler (for mobile and desktop)
  onInsertRowClick(event: Event) {
    event.stopPropagation();
    this.insertActionClick.emit({ insertAtIndex: 0, action: InsertActionType.Exercise });
  }

  // Combo set insert indicator methods (desktop only)

  // get indices for insert zones (between each pair of cards, starting from index 1)
  // index 0 is handled by the add-exercise-row above
  getInsertIndicatorIndices(): number[] {
    if (!this.exercises || this.exercises.length < 2) return [];
    // returns [1, 2, ...] for positions between cards (not above first card)
    return Array.from({ length: this.exercises.length - 1 }, (_, i) => i + 1);
  }

  // place the insert strips from where cs-card-list actually put the cards (#6111). They used
  // to be read from the page during this template's check, which runs before cs-card-list moves
  // the cards, so a strip could stay over the first card until something re-checked this list.
  onCardPlacementsChanged(placements: CsCardListPlacement[]): void {
    const placementByElement = new Map(placements.map(p => [p.element, p]));
    const cards = Array.from(this.elementRef.nativeElement.querySelectorAll('cs-card.exercise-card')) as HTMLElement[];
    const tops: (number | undefined)[] = [];
    // for each gap, the strip sits after the card above it (insert index i follows card i-1)
    for (let i = 1; i < cards.length; i++) {
      const above = placementByElement.get(cards[i - 1]);
      tops[i] = above ? above.top + above.height + 6 : undefined; // centre of the 12px gap
    }
    this.insertZoneTops.set(tops);
  }

  onInsertZoneHover(index: number, isHovering: boolean) {
    this.hoveredInsertIndex = isHovering ? index : null;
    this.cdRef.markForCheck();
  }

  // handle click on insert indicator
  onInsertIndicatorClick(event: Event, insertAtIndex: number) {
    event.stopPropagation();
    this.insertActionClick.emit({ insertAtIndex, action: InsertActionType.Exercise });
  }
}
